import { useEffect, useState } from 'react';

// Reads the OS-level `prefers-reduced-motion` setting and reacts to
// runtime changes (settings panel toggles in modern browsers fire a
// MediaQueryList change event). Returns `true` when the user has
// asked the system to reduce motion.
//
// Use to gate JS-driven timers (auto-advancing carousels, animated
// onboarding) and as a CSS escape hatch where pure CSS @media
// (prefers-reduced-motion) is not available (e.g. SVG <animate>
// children that ignore CSS motion preferences).
//
// SSR-safe: returns `false` on the server / before mount.

export default function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    // addEventListener is the modern API; addListener is the legacy
    // Safari < 14 fallback. Both are kept to avoid breaking older
    // devices the app explicitly targets.
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener)   mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else if (mq.removeListener) mq.removeListener(apply);
    };
  }, []);

  return reduced;
}
